// File: src/pages/AddRoleNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Label, Input, Form } from "reactstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

// ✅ Follow your path rules
import {
  CreateRole,
  CreateRoleByMaster,
} from "../import/ApiEndPoint/ApiCalling";
// Roles config you already have
import { Roles, NormalRoles } from "../import/AddRole";

const injectOnce = () => {
  if (document.getElementById("addrole-inline-css")) return;
  const s = document.createElement("style");
  s.id = "addrole-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .grp-row{ border:1px solid #e5e7eb; border-radius:10px; overflow:hidden; }
    .grp-head{ display:flex; align-items:center; gap:10px; padding:10px 12px; background:#f4f7f6; cursor:pointer; }
    .grp-head:hover{ background:#f1f5f9; }
    .grp-title{ font-weight:700; }
    .pill{ border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; background:#fff; }
    .perm-row{ display:flex; align-items:center; gap:8px; padding:8px 12px; border-top:1px solid #f1f5f9; }
    .perm-col{ flex:1; display:flex; justify-content:center; }
    .perm-name{ flex:2; text-align:left; font-weight:600; }
    .toggle{ min-width:20px; font-weight:800; user-select:none; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

/** Build a map of what the CURRENT SuperAdmin is allowed to see/select */
const buildAllowMap = (rolePermission = []) => {
  const map = new Map(); // tabTitle -> Set(perms)
  rolePermission.forEach(({ pagename, permission }) => {
    if (!pagename) return;
    map.set(pagename, new Set(Array.isArray(permission) ? permission : []));
  });
  return map;
};

const ALL_PERMS = [
  "View",
  "Create",
  "Edit",
  "Delete",
  "Download",
  "BulkUpload",
];
const allNA = (arr = []) => arr.every((p) => p === "NA");

/**
 * Take Roles/NormalRoles matrix and:
 *  1) mask perms not allowed to the user with "NA"
 *  2) remove tabs that end up with no selectable perms
 *  3) remove groups that end up with zero tabs
 */
const filterMatrixByAllow = (matrix, allowMap) => {
  const filteredGroups = (matrix || []).map((group) => {
    const nextTabs = (group?.TabName || [])
      .map((tab) => {
        const allowed = allowMap.get(tab?.title);
        if (!allowed) {
          return {
            ...tab,
            permission: ["NA", "NA", "NA", "NA", "NA", "NA"],
          };
        }
        const original = Array.isArray(tab?.permission) ? tab.permission : [];
        // Align to ALL_PERMS order while preserving existing positions
        // We only mask; we do not reorder the incoming matrix.
        const masked = original.map((p) =>
          p === "NA" ? "NA" : allowed.has(p) ? p : "NA"
        );
        return { ...tab, permission: masked };
      })
      // remove tabs with all NA
      .filter((tab) => !allNA(tab.permission));

    return { ...group, TabName: nextTabs };
  });

  // remove groups that now have zero tabs
  return filteredGroups.filter(
    (g) => Array.isArray(g.TabName) && g.TabName.length > 0
  );
};

// Selected shape: [{ pagename: "Tab Title", permission: ["View","Create",...]}]
export default function AddRoleNew() {
  const navigate = useNavigate();
  const [desc, setDesc] = useState("");
  const [role, setRole] = useState("");
  const [database, setDatabase] = useState("");
  const [isMaster, setIsMaster] = useState(false);

  const [groups, setGroups] = useState([]); // Roles/NormalRoles (possibly filtered)
  const [expanded, setExpanded] = useState({}); // { [groupIndex]: boolean }
  const [selected, setSelected] = useState([]); // [{ pagename, permission[] }]

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const master = user?.rolename?.roleName === "MASTER";
      setIsMaster(master);

      // Base matrix (Master sees Roles, non-master sees NormalRoles)
      const baseMatrix = master ? Roles || [] : NormalRoles || [];

      // If SuperAdmin (not MASTER): mask AND remove empty tabs/groups
      if (!master) {
        const allow = buildAllowMap(user?.rolename?.rolePermission || []);
        setGroups(filterMatrixByAllow(baseMatrix, allow));
      } else {
        setGroups(baseMatrix);
      }
    } catch {
      // Fallback to NormalRoles if anything goes wrong
      setGroups(NormalRoles || []);
    }
  }, []);

  const selectedCount = useMemo(() => {
    // count total checked permissions
    return selected.reduce(
      (acc, item) =>
        acc + (Array.isArray(item.permission) ? item.permission.length : 0),
      0
    );
  }, [selected]);

  const toggleGroup = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const getTabPerm = (tabTitle) => {
    const found = selected.find((x) => x.pagename === tabTitle);
    return found?.permission || [];
  };

  const setTabPerm = (tabTitle, nextPerms) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.pagename === tabTitle);
      // filter out NA to avoid selecting hidden perms
      const cleaned = nextPerms.filter((p) => p && p !== "NA");
      if (cleaned.length === 0) {
        return prev.filter((x) => x.pagename !== tabTitle);
      }
      if (exists) {
        return prev.map((x) =>
          x.pagename === tabTitle ? { ...x, permission: cleaned } : x
        );
      }
      return [...prev, { pagename: tabTitle, permission: cleaned }];
    });
  };

  const onTogglePerm = (tabTitle, permit, checked) => {
    const current = getTabPerm(tabTitle);
    if (checked) {
      if (!current.includes(permit)) setTabPerm(tabTitle, [...current, permit]);
    } else {
      setTabPerm(
        tabTitle,
        current.filter((p) => p !== permit)
      );
    }
  };

  const onSelectAllInGroup = (group, checked) => {
    // Apply to every tab under the group; add all non-NA perms if checked, else clear.
    (group?.TabName || []).forEach((tab) => {
      const all = (tab?.permission || []).filter((p) => p !== "NA");
      setTabPerm(tab?.title, checked ? all : []);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!role.trim()) return swal("Role name is required");
    if (!desc.trim()) return swal("Role description is required");

    const filteredValue = role.trim().split(" ")[0];

    try {
      if (isMaster) {
        if (!database.trim()) return swal("Database is required for MASTER");

        // ✅ Wrap in Role: [ ... ]
        const payload = {
          Role: [
            {
              createdBy: user?._id,
              roleName: role,
              id: filteredValue,
              position: 1,
              database,
              desc,
              rank: 1,
              rolePermission: selected, // [{ pagename, permission: [] }]
            },
          ],
        };

        const res = await CreateRoleByMaster(payload);
        if (res) {
          swal("Created Successfully");
          navigate(-1);
          setSelected([]);
          setDesc("");
          setRole("");
          setDatabase("");
        }
      } else {
        // ✅ Wrap in Role: [ ... ]
        const payload = {
          Role: [
            {
              createdBy: user?._id,
              roleName: role,
              id: filteredValue,
              desc,
              rolePermission: selected,
            },
          ],
        };

        const res = await CreateRole(payload);
        if (res) {
          swal("Created Successfully");
          navigate(-1);
          setSelected([]);
          setDesc("");
          setRole("");
        }
      }
    } catch (err) {
      console.error(err);
      swal("Failed to create role");
    }
  };

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Create Role <span className="count-pill">{selectedCount}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate("/app/freshlist/account/RoleList")}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Form Card ===== */}
      <Row>
        <Col xl={12}>
          <Card className="p-card" style={{ padding: 14 }}>
            <Form onSubmit={handleSubmit}>
              <Row style={{ gap: 12 }}>
                <Col md="4" sm="12">
                  <Label>Enter Role Name *</Label>
                  <Input
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    type="text"
                    placeholder="Enter Role Name"
                  />
                </Col>
                <Col md="6" sm="12">
                  <Label>Enter Role Description *</Label>
                  <Input
                    required
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    type="text"
                    placeholder="Enter Role Description"
                  />
                </Col>
                {isMaster && (
                  <Col md="4" sm="12">
                    <Label>Database *</Label>
                    <Input
                      required
                      value={database}
                      onChange={(e) => setDatabase(e.target.value)}
                      type="text"
                      placeholder="Enter database name"
                    />
                  </Col>
                )}
              </Row>

              {/* ===== Permission Groups ===== */}
              <div style={{ marginTop: 14 }}>
                {Array.isArray(groups) &&
                  groups.map((group, gi) => {
                    const isOpen = !!expanded[gi];

                    // Determine if group is "fully selected"
                    const allPermsInGroup = (group?.TabName || []).flatMap(
                      (t) => (t?.permission || []).filter((p) => p !== "NA")
                    );
                    const tabTitles = (group?.TabName || []).map(
                      (t) => t?.title
                    );
                    const selectedInGroup = selected
                      .filter((s) => tabTitles.includes(s.pagename))
                      .flatMap((s) => s.permission);
                    const allChecked =
                      allPermsInGroup.length > 0 &&
                      allPermsInGroup.every((p) => selectedInGroup.includes(p));

                    return (
                      <div
                        key={gi}
                        className="grp-row"
                        style={{ marginBottom: 10 }}
                      >
                        <div
                          className="grp-head"
                          onClick={() => toggleGroup(gi)}
                        >
                          <span className="toggle">{isOpen ? "▾" : "▸"}</span>
                          <span className="grp-title">
                            {group?.title || "-"}
                          </span>
                          <span className="pill">
                            {(group?.TabName || []).length} screens
                          </span>
                          <div
                            style={{
                              marginLeft: "auto",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) =>
                                onSelectAllInGroup(group, e.target.checked)
                              }
                              title="Select all in this group"
                              style={{ width: 18, height: 18 }}
                            />
                            <span style={{ fontSize: 12 }}>Select All</span>
                          </div>
                        </div>

                        {isOpen && (
                          <div>
                            {/* Header row */}
                            <div
                              className="perm-row"
                              style={{ background: "#fafafa", fontWeight: 700 }}
                            >
                              <div className="perm-name">Screen</div>
                              <div className="perm-col">View</div>
                              <div className="perm-col">Create</div>
                              <div className="perm-col">Edit</div>
                              <div className="perm-col">Delete</div>
                              {/** Extra columns appear for MASTER-only permissions */}
                              {isMaster && (
                                <div className="perm-col">Download</div>
                              )}
                              {isMaster && (
                                <div className="perm-col">BulkUpload</div>
                              )}
                            </div>

                            {(group?.TabName || []).map((tab, ti) => {
                              const tabTitle = tab?.title;
                              const perms = Array.isArray(tab?.permission)
                                ? tab.permission
                                : [];

                              // For safety: if a tab somehow has no selectable perms, skip rendering it
                              if (allNA(perms)) return null;

                              const current = getTabPerm(tabTitle);
                              return (
                                <div key={ti} className="perm-row">
                                  <div className="perm-name">{tabTitle}</div>
                                  {[
                                    "View",
                                    "Create",
                                    "Edit",
                                    "Delete",
                                    ...(isMaster
                                      ? ["Download", "BulkUpload"]
                                      : []),
                                  ].map((permLabel, pi) => {
                                    // show empty cell if "NA" in the source defs
                                    const isNA =
                                      perms[pi] === "NA" ||
                                      !perms.includes(permLabel);
                                    if (isNA)
                                      return (
                                        <div
                                          key={pi}
                                          className="perm-col"
                                        ></div>
                                      );
                                    const checked = current.includes(permLabel);
                                    return (
                                      <div key={pi} className="perm-col">
                                        <Input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) =>
                                            onTogglePerm(
                                              tabTitle,
                                              permLabel,
                                              e.target.checked
                                            )
                                          }
                                          style={{ width: 18, height: 18 }}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 14,
                }}
              >
                <Button type="submit" color="primary">
                  Submit
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
